import {
  IArrangeWordsTaskItemSession,
  IArrangeWordsTaskSession,
  IPlayArrangeWordsTask,
  tArrangeWordsAnswer
} from 'common/modules/ReactShared';
import { t } from 'i18n';
import { useCallback, useEffect } from 'react';
import { usePromptContext } from 'students/views/shared/hooks';
import styled from 'styled-components';
import Streak from '../../common/components/Streak';
import { usePlayAnswers, useStreakFactor } from '../../common/hooks';
import PlayTaskScreenSwitcher from '../../common/screens/PlayTaskScreenSwitcher';
import { IPlayTaskProps, tPlayTaskComponentType } from '../types';
import PlayArrangeWordsMainScreen from './PlayArrangeWordsMainScreen';
import leftBg from './assets/arrangeWordsBgLeft.svg';
import rightBg from './assets/arrangeWordsBgRight.svg';
import { ISelectAnswerPayload } from './components/ArrangeWordsItem';

const PlayArrangeWordsTask: tPlayTaskComponentType<
  IPlayTaskProps<IPlayArrangeWordsTask, IArrangeWordsTaskSession>
> = ({ task, taskSession, isCompleting, onFinish, onNext, onAnswer, className }) => {
  const { items, instruction } = task;
  const answersFn = usePlayAnswers<tArrangeWordsAnswer>();
  const { streakNumber, streakPercent, onTheFirstTry } = useStreakFactor();
  const { makeDirty } = usePromptContext();

  useEffect(() => {
    if (taskSession?.taskItemSessions) {
      answersFn(taskSession.taskItemSessions);
    } else {
      answersFn(
        items.map((item, index) => {
          return {
            id: index,
            completed: false,
            words: [],
            taskItemId: item.id,
            mistakesCount: 0,
            skipped: false,
            url: ''
          };
        })
      );
    }

    onTheFirstTry(false);
  }, [taskSession, items, onTheFirstTry, answersFn]);

  const updateAnswer = (itemId: number, data: Partial<tArrangeWordsAnswer[number]>) => {
    const answerIndex = answersFn().findIndex(
      (answer: IArrangeWordsTaskItemSession) => answer.taskItemId === itemId
    );

    const { mistakesCount, skipped } = data;

    if (answerIndex < 0) return;

    const answer = answersFn()[answerIndex];
    const newAnswerData = { ...answer, ...data };

    answersFn([
      ...answersFn().slice(0, answerIndex),
      newAnswerData,
      ...answersFn().slice(answerIndex + 1)
    ]);

    onAnswer && onAnswer(newAnswerData);

    onTheFirstTry(!(mistakesCount || skipped));
  };

  const handleSelectAnswer = (payload: ISelectAnswerPayload) => {
    const { itemId, mistakesCount, itemIndex } = payload;
    const item = items[itemIndex];

    updateAnswer(itemId, {
      words: item.solution.trim().split(' '),
      mistakesCount,
      skipped: false
    });

    makeDirty(true);
  };

  const handleSkip = (itemId: number) => {
    updateAnswer(itemId, {
      skipped: true
    });
  };

  const handleFinishTask = () => {
    onNext && onNext();
  };

  const submitTask = useCallback(() => {
    onFinish && onFinish(answersFn());
  }, [onFinish, answersFn]);

  const handleCompleteTask = (goToFinishScreen: () => void) => () => {
    submitTask();

    goToFinishScreen();
  };

  return (
    <>
      <Streak streakNumber={streakNumber} streakPercent={streakPercent} />
      <PlayTaskScreenSwitcher
        isCompleting={isCompleting}
        onExitFinishScreen={handleFinishTask}
        className={className}
        startScreenHeading={
          instruction ||
          t('frontend.lesson_task.tasks.play.arrange_words.start_screen.heading')
        }
        itemsExist={!!items.length}
        taskStatus={taskSession?.status}
        completeTask={submitTask}
        taskColor="#4f398e"
      >
        {(goToFinishScreen) => (
          <PlayArrangeWordsMainScreen
            task={task}
            onSelectAnswer={handleSelectAnswer}
            onComplete={handleCompleteTask(goToFinishScreen)}
            onSkip={handleSkip}
          />
        )}
      </PlayTaskScreenSwitcher>
    </>
  );
};

export default PlayArrangeWordsTask;

PlayArrangeWordsTask.backgroundComponent = styled.div`
  background-color: #4f398e;

  @media (min-width: ${({ theme }) => theme.linguBptLg}) {
    background-image: url(${leftBg}), url(${rightBg});
    background-repeat: no-repeat, no-repeat;
    background-position-x: left, right;
    background-size: contain;
  }
`;
PlayArrangeWordsTask.bgColor = '#4f398e';
